import { useId } from 'react';

import { Field } from '@/components/ui';
import { cn } from '@/lib/cn';

import { MAX_COPIES, MIN_COPIES, clampCopies } from './copies';

export interface CopiesFieldProps {
  value: number;
  onChange: (copies: number) => void;
  disabled?: boolean;
  /**
   * Label on the same line and no hint, for the copy nested in a file's card.
   * The BUTTONS stay 44px: compact is about the words around the control, not
   * about shrinking something a thumb has to hit.
   */
  compact?: boolean;
}

const stepButton = cn(
  'grid h-touch w-touch shrink-0 place-items-center rounded-control border border-line-strong',
  'bg-surface text-xl font-semibold text-ink',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2',
  'disabled:cursor-not-allowed disabled:opacity-50',
);

/**
 * Copies, as a −/+ stepper around a real number input.
 *
 * Both halves matter on a phone: the buttons are 44px targets for the common
 * case (1 → 2 → 3), and the input is still there for the customer who needs 40
 * and should not have to tap thirty-nine times. `inputMode="numeric"` brings up
 * the digit keypad without the spinner arrows a `type="number"` shows on
 * desktop, which are far below 44px and unusable with a thumb.
 */
export function CopiesField({
  value,
  onChange,
  disabled = false,
  compact = false,
}: CopiesFieldProps) {
  const compactId = useId();

  const stepper = (id: string, describedBy?: string) => (
    <div className="flex items-center gap-2">
      <button
        type="button"
        className={stepButton}
        onClick={() => onChange(clampCopies(value - 1))}
        disabled={disabled || value <= MIN_COPIES}
        aria-label="One fewer copy"
      >
        <span aria-hidden="true">−</span>
      </button>

      <input
        id={id}
        aria-describedby={describedBy}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="off"
        value={String(value)}
        disabled={disabled}
        onChange={(event) => {
          const digits = event.target.value.replace(/[^\d]/g, '');
          // An empty box is a legitimate intermediate state while typing, so
          // it reads as 1 rather than snapping the caret around.
          onChange(digits === '' ? MIN_COPIES : clampCopies(Number.parseInt(digits, 10)));
        }}
        className={cn(
          'min-h-touch rounded-control border border-line-strong bg-surface px-3 py-2',
          'text-center text-base tabular-nums text-ink',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2',
          compact ? 'w-14' : 'w-20',
        )}
      />

      <button
        type="button"
        className={stepButton}
        onClick={() => onChange(clampCopies(value + 1))}
        disabled={disabled || value >= MAX_COPIES}
        aria-label="One more copy"
      >
        <span aria-hidden="true">+</span>
      </button>
    </div>
  );

  if (compact) {
    return (
      <div className="flex items-center justify-between gap-3">
        <label htmlFor={compactId} className="text-xs font-medium text-ink">
          Copies
        </label>
        {stepper(compactId)}
      </div>
    );
  }

  return (
    <Field label="Copies" hint={`Between ${MIN_COPIES} and ${MAX_COPIES}`}>
      {({ id, describedBy }) => stepper(id, describedBy)}
    </Field>
  );
}
